import React, { useState, useRef, createElement, Component } from 'react';
import { TabsRoot, TabsList, TabsTrigger, TabsContent } from './components/ui/Tabs';
import { CourseTab } from './components/course/CourseTab';
import { GpxImportScreen } from './components/course/GpxImportScreen';
import { PacingTable } from './components/pacing/PacingTable';
import { NutritionTable } from './components/nutrition/NutritionTable';
import { SettingsTab } from './components/settings/SettingsTab';
import { CalibrationWizard } from './components/settings/CalibrationWizard';
import { useRaceStore } from './stores/raceStore';
import { useTranslation, LOCALE_OPTIONS } from './utils/i18n';
import { ConfirmDialog } from './components/ui/ConfirmDialog';
import { MapIcon, TimerIcon, SettingsIcon, ZapIcon, XIcon, UtensilsIcon, DownloadIcon, UploadIcon, HelpCircleIcon } from 'lucide-react';
import { HelpPanel } from './components/help/HelpPanel';
import { trackTabViewed, trackPlanExported, trackPlanImported } from './utils/analytics';
export function App() {
  const {
    t,
    locale,
    setLocale
  } = useTranslation();
  const [activeTab, setActiveTab] = useState('course');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [importStatus, setImportStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [showNewPlanConfirm, setShowNewPlanConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    gpxData,
    settings,
    segments,
    pacingResults,
    nutritionPlans,
    exportRacePlan,
    importRacePlan,
    updateAthleteCalibration
  } = useRaceStore();
  // 1. No Data -> Import Screen
  if (!gpxData) return <GpxImportScreen data-id="element-0" />;
  // 2. Data exists but not calibrated -> Mandatory Wizard
  // We render the main app in background (blurred?) or just the wizard
  // Let's render the app but overlay the wizard modally
  const showMandatoryCalibration = !settings.athleteProfile.isCalibrated;
  const handleExport = () => {
    const json = exportRacePlan();
    const blob = new Blob([json], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(settings.raceName || 'race-plan').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    const lastResult = pacingResults[pacingResults.length - 1];
    const totalTimeHours = lastResult ? lastResult.accumulatedTime / 60 : 0;
    const checkpointCount = segments.filter(s => s.checkpointType !== 'None').length;
    const hasNutrition = Object.values(nutritionPlans).some(p => p.intakeItems && p.intakeItems.length > 0);
    trackPlanExported({
      segment_count: segments.length,
      distance_km: gpxData?.totalDistance ?? 0,
      elevation_gain_m: Math.round(gpxData?.totalElevationGain ?? 0),
      total_time_hours: Math.round(totalTimeHours * 10) / 10,
      pacing_model: settings.activeModelId,
      scenario: settings.executionScenario,
      has_nutrition_plan: hasNutrition,
      checkpoint_count: checkpointCount
    });
  };
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const result = importRacePlan(event.target?.result as string);
      trackPlanImported(result.success);
      setImportStatus({
        type: result.success ? 'success' : 'error',
        message: result.message
      });
      setTimeout(() => setImportStatus(null), 5000);
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  const currentLocale = LOCALE_OPTIONS.find(l => l.value === locale)!;
  return <div className="min-h-screen bg-[#F7F7FA] text-[#2D2D2D] pb-24 lg:pb-8 relative" data-id="element-1">
      {/* Mandatory Calibration Overlay */}
      {showMandatoryCalibration && <div className="fixed inset-0 z-[100] bg-white/80 backdrop-blur-sm flex items-center justify-center" data-id="element-2">
          <CalibrationWizard initialAnswers={settings.athleteProfile.answers} onSave={answers => updateAthleteCalibration(answers)} onCancel={() => {
        // Cannot cancel mandatory calibration for new race
        // Ideally we might allow cancelling to go back to import screen?
        // For now, we just don't allow closing it without saving.
      }} data-id="element-3" />
        </div>}

      {/* Header */}
      <header className="bg-white border-b border-[#E8E8ED] sticky top-0 z-40" data-id="element-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5">
          <div className="flex items-center justify-between h-16" data-id="element-6">
            <div className="flex items-center gap-3 min-w-0 flex-1 mr-2" data-id="element-7">
              <div className="w-8 h-8 bg-[#FC4C02] rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-8">
                <ZapIcon className="h-4 w-4 text-white" data-id="element-9" />
              </div>
              <div className="min-w-0" data-id="element-10">
                <h1 className="text-base font-semibold text-[#2D2D2D] truncate" data-id="element-11">
                  {settings.raceName || 'Trail Race Planner'}
                </h1>
                <p className="text-xs text-[#9B9BA7] hidden lg:block" data-id="element-12">
                  {gpxData.totalDistance.toFixed(1)} km •{' '}
                  {Math.round(gpxData.totalElevationGain)}m D+
                </p>
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-13">
              {/* Language Switcher */}
              <div className="relative" data-id="element-14">
                <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 text-[#6B6B76] hover:text-[#2D2D2D] px-2 py-1.5 rounded-lg hover:bg-[#F7F7FA] transition-colors text-sm" data-id="element-15">
                  <currentLocale.FlagComponent className="w-5 h-3.5 rounded-[2px] object-cover" data-id="element-16" />
                  <span className="hidden sm:inline text-xs font-medium" data-id="element-17">
                    {currentLocale.value.toUpperCase()}
                  </span>
                </button>
                {langOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} data-id="element-18" />
                    <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-[#E8E8ED] rounded-xl shadow-lg py-1 min-w-[140px]" data-id="element-19">
                      {LOCALE_OPTIONS.map(opt => <button key={opt.value} onClick={() => {
                    setLocale(opt.value);
                    setLangOpen(false);
                  }} className={`w-full text-left px-3 py-2 flex items-center gap-2.5 text-sm hover:bg-[#F7F7FA] transition-colors ${locale === opt.value ? 'font-semibold text-[#FC4C02]' : 'text-[#2D2D2D]'}`} data-id="element-20">
                          <opt.FlagComponent className="w-5 h-3.5 rounded-[2px]" data-id="element-21" />
                          {opt.label}
                        </button>)}
                    </div>
                  </>}
              </div>
              <div className="w-px h-6 bg-[#E8E8ED] mx-0.5" data-id="element-22" />
              <button onClick={handleExport} title={t('header.export')} className="text-[#9B9BA7] hover:text-[#FC4C02] p-2 rounded-lg hover:bg-[#FC4C02]/5 transition-colors" data-id="element-23">
                <DownloadIcon className="h-5 w-5" data-id="element-24" />
              </button>
              <button onClick={() => fileInputRef.current?.click()} title={t('header.import')} className="text-[#9B9BA7] hover:text-[#FC4C02] p-2 rounded-lg hover:bg-[#FC4C02]/5 transition-colors" data-id="element-25">
                <UploadIcon className="h-5 w-5" data-id="element-26" />
              </button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" data-id="element-27" />
              <div className="w-px h-6 bg-[#E8E8ED] mx-0.5" data-id="element-28" />
              {/* Help Button */}
              <button onClick={() => setIsHelpOpen(true)} title={t('header.help')} className="text-[#9B9BA7] hover:text-[#FC4C02] p-2 rounded-lg hover:bg-[#FC4C02]/5 transition-colors" data-id="element-29">
                <HelpCircleIcon className="h-5 w-5" data-id="element-30" />
              </button>
              <button className="text-[#9B9BA7] hover:text-[#2D2D2D] p-2 rounded-lg hover:bg-[#F7F7FA] transition-colors" onClick={() => setShowNewPlanConfirm(true)} data-id="element-31">
                <XIcon className="h-5 w-5" data-id="element-32" />
              </button>
            </div>
          </div>
        </div>
        {importStatus && <div className={`px-4 py-2 text-xs font-semibold text-center ${importStatus.type === 'success' ? 'bg-[#ECFDF5] text-[#00B843]' : 'bg-[#FEF2F2] text-[#E34402]'}`} data-id="element-33">
            {importStatus.message}
          </div>}
      </header>

      {/* Desktop Tabs */}
      <div className="hidden lg:block sticky top-16 z-30 bg-white border-b border-[#E8E8ED]" data-id="element-34">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-35">
          <TabsRoot value={activeTab} onValueChange={tab => {
          setActiveTab(tab);
          trackTabViewed(tab);
        }} data-id="element-36">
            <TabsList data-id="element-37">
              <TabsTrigger value="course" data-id="element-38">
                <MapIcon className="h-4 w-4 mr-2" data-id="element-39" /> {t('nav.route')}
              </TabsTrigger>
              <TabsTrigger value="pacing" data-id="element-40">
                <TimerIcon className="h-4 w-4 mr-2" data-id="element-41" /> {t('nav.pacing')}
              </TabsTrigger>
              <TabsTrigger value="nutrition" data-id="element-42">
                <UtensilsIcon className="h-4 w-4 mr-2" data-id="element-43" /> {t('nav.nutrition')}
              </TabsTrigger>
              <TabsTrigger value="settings" data-id="element-44">
                <SettingsIcon className="h-4 w-4 mr-2" data-id="element-45" /> {t('nav.settings')}
              </TabsTrigger>
            </TabsList>
          </TabsRoot>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-46">
        <TabsRoot value={activeTab} onValueChange={tab => {
        setActiveTab(tab);
        trackTabViewed(tab);
      }} data-id="element-47">
          <TabsContent value="course" data-id="element-48">
            <CourseTab data-id="element-49" />
          </TabsContent>
          <TabsContent value="pacing" data-id="element-50">
            <div className="space-y-6" data-id="element-51">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4" data-id="element-52">
                <div data-id="element-53">
                  <h2 className="text-2xl font-bold text-[#2D2D2D]" data-id="element-54">
                    {t('pacing.title')}
                  </h2>
                  <p className="text-[#6B6B76] text-sm mt-1" data-id="element-55">
                    {t('pacing.subtitle')}
                  </p>
                </div>
              </div>
              <PacingTable data-id="element-56" />
            </div>
          </TabsContent>
          <TabsContent value="nutrition" data-id="element-57">
            <div className="space-y-6" data-id="element-58">
              <div data-id="element-59">
                <h2 className="text-2xl font-bold text-[#2D2D2D]" data-id="element-60">
                  {t('nutrition.title')}
                </h2>
                <p className="text-[#6B6B76] text-sm mt-1" data-id="element-61">
                  {t('nutrition.subtitle')}
                </p>
              </div>
              <NutritionTable data-id="element-62" />
            </div>
          </TabsContent>
          <TabsContent value="settings" data-id="element-63">
            <SettingsTab data-id="element-64" />
          </TabsContent>
        </TabsRoot>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E8ED] z-50 safe-area-inset-bottom" data-id="element-65">
        <div className="grid grid-cols-4 h-16" data-id="element-66">
          {[{
          id: 'course',
          icon: MapIcon,
          label: t('nav.route')
        }, {
          id: 'pacing',
          icon: TimerIcon,
          label: t('nav.pacing')
        }, {
          id: 'nutrition',
          icon: UtensilsIcon,
          label: t('nav.nutrition')
        }, {
          id: 'settings',
          icon: SettingsIcon,
          label: t('nav.settings')
        }].map(tab => <button key={tab.id} onClick={() => {
          setActiveTab(tab.id);
          trackTabViewed(tab.id);
        }} className={`flex flex-col items-center justify-center gap-1 transition-colors ${activeTab === tab.id ? 'text-[#FC4C02]' : 'text-[#9B9BA7]'}`} data-id="element-67">
              <tab.icon className="h-5 w-5" data-id="element-68" />
              <span className="text-xs font-medium" data-id="element-69">{tab.label}</span>
            </button>)}
        </div>
      </nav>

      <ConfirmDialog isOpen={showNewPlanConfirm} onClose={() => setShowNewPlanConfirm(false)} onConfirm={() => {
      localStorage.clear();
      window.location.reload();
    }} title={t('confirm.newPlan.title')} message={t('confirm.newPlan.message')} confirmLabel={t('confirm.newPlan.confirm')} cancelLabel={t('common.cancel')} variant="danger" data-id="element-70" />

      <HelpPanel isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} data-id="element-71" />
    </div>;
}